import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import BlogPublishInfo from '../BlogPublishInfo.tsx';
import testDrivenDevelopmentPng from "../../static/blog_test_driven_development_main_image.png";
import softwareDevelopmentLifecyclePng from "../../static/traditional_software_lifecycle.png";
import testDrivenSoftwareDevelopmentLifecyclePng from "../../static/test_driven_development.png";
import integrationTestsPng from "../../static/integration_tests.png";
import ImageListItem from '@mui/material/ImageListItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Alert from '@mui/material/Alert';
import { CodeBlock, dracula } from 'react-code-blocks';

const codeUnitTests = `
//Filename : postAppMicroservice.js
const express = require('express');
const postService = express();
const db = require('dbHandler);//abstracted away

// Functionality linked to endpoint to create a POST named 
const createPost = (req, res) => {
  try {
    const { body } = req;
    if(!body?.title) {
      res.status(400).json({
       message: "Title is missing",
      });
    }
    //Stores The Post Details in DB at this step
    db.store({
      title: body.title,
    })
    res.json({
      message: "Post created successfully",
    });
  } catch(err) {
    res.status(500).json({
      message: "Post creation failed",
    });
  }
} 

postService.post("/api/create", createPost);

//Filename: post.test.js
describe("UNIT TEST: POST /api/create", () => {
  it("HAPPY FLOW: should create a post", async () => {
    const res = await request(app).post("/api/create"
    ).send({
      title: "Test Post 01"
    });
    expect(res.statusCode).toBe(200);
    expect(res.body.message).toBe("Post created successfully");
  });

   it("BROKEN FLOW: should not create a post", async () => {
    const res = await request(app).post(
      "/api/create"
    ).send({
      name: "Test Post 01"
    });
    expect(res.statusCode).toBe(400);
    expect(res.body.message).toBe("Title is missing");
  });
});
`;

const codeIntegrationTests = `
//Filename : postAppMicroservice.js
const express = require('express');
const postService = express();
let request = require('async-request');

const db = require('dbHandler);//abstracted away

// Functionality linked to endpoint to create a POST named 
const createPost = async (req, res) => {
  try {
    const { body } = req;
    await request('https://mock.backend/api/validate/post', {
      method: 'GET',
      data: {
        title: body?.title,
      }
    });
    //Stores The Post Details in DB at this step
    db.store({
      title: body.title,
    })
    res.json({
      message: "Post created successfully",
    });
  } catch(err) {
    res.status(err.code || 500).json({
      message: err.message || "Post creation failed",
    });
  }
}
postService.post("/api/create", createPost);

//Filename : validatorMicroservice.js
const express = require('express');
const validatorService = express();

// Functionality linked to endpoint to create a POST named 
const validatePost = (req, res) => {
  try {
    const { body } = req;
     if(!body?.title) {
      res.status(400).json({
        message: "Title Validation Failed",
      });
    }
    res.status(200).json({
      validated: 1,
    });
  } catch(err) {
    res.status(500).json({
      message: "Validation Failed",
    });
  }
} 
validatorService.get("/api/validate/post", validatePost);

//Filename: postvalidation.test.js
describe("INTEGRATION TEST: POST /api/create with two services involved", () => {
  it("HAPPY FLOW: should create a post", async () => {
    const res = await request(app).post("/api/create"
    ).send({
      title: "Test Post 01"
    });
    expect(res.statusCode).toBe(200);
    expect(res.body.message).toBe("Post created successfully");
  });

   it("BROKEN FLOW: should not create a post", async () => {
    const res = await request(app).post(
      "/api/create"
    ).send({
      name: "Test Post 01"
    });
    expect(res.statusCode).toBe(400);
    expect(res.body.message).toBe("Title Validation Failed");
  });
});
`;

export default function TestDrivenDevelopmentBlog04() {
    return (
        <Container id="TestDrivenDevelopmentBlog04" sx={{ my: { sm: 0, xs: 5 }}} fixed>
          <Box sx={{  minHeight: '50vh' }}>
              <Typography variant="h1" component="h1" align="center">
                Test Driven Development in Node.js
              </Typography>
              <BlogPublishInfo date="13th Feb 2025"/>
              <Divider />
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '6vw'} }}>
                <img
                    srcSet={testDrivenDevelopmentPng}
                    alt={"testDrivenDevelopment"}
                    loading="lazy"
                  />
              </ImageListItem>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Overview
              </Typography>
              <Typography variant="body1" align="left">
                Unit tests and Integration tests help us uphold the quality of code in the long run of project lifetime and helps in prevention/early detection of critical bugs during development and feature upgrades.
                Testing is a critical aspect of software development and creates a agreement that bugs are normal part of feature lifecycle and testing helps us reduce/avoid impact of bugs on end users. 
                This article will cover the common practises associated with tests and give an overview of Test Driven Development approach vs traditional Approach, the content is purely based on my experience in the software industry and there would be difference of opinions around testing, constructive criticism is always welcome to help us improve.
              </Typography>
              <br/>
              In a glance, real life software development flow with tests looks as follows:
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '6vw'} }}>
                <img
                    srcSet={softwareDevelopmentLifecyclePng}
                    alt={"softwareDevelopmentLifecycle"}
                    loading="lazy"
                  />
              </ImageListItem>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                What are Unit Tests?
              </Typography>
              <Typography variant="body1" align="left">
                Unit tests are a type of feature tests that verfiy that functionality works standalone as intended by the feature requirements.
               <br/>
                they are usually written for functions, methods, classes, standalone microservices in Node.JS to ensure functionality, APIs work as intended.
              </Typography>
                Before Quality Assurance Team checks the feature, this is the first level of Software Testing which helps in early detection/prevention of bugs/unintended sideeffects of new features/functionality changes.
                <br/><br/> 
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Why Unit Tests and what to cover?
              </Typography>
              <Typography variant="body1" align="left">
                <b>What compels us</b> to choose to write tests which consume precious development time and adds more time to delivery timelines?
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                <ListItem sx={{ display: 'list-item' }}>
                    If a product has no bug(s) being reported that means something else is going wrong with our product and needs to retrospect.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    We need to validate the developed functionality works correctly when a new feature is added or 
                    when existing functionality is modified over time need to check the changes are non-breaking.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    Unit Tests Increases delivery speed as unit tests enhance confidence in the functionality working correctly after making changes reducing QA loops
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    Early detection of issues if any by extensive coverage including both happy paths and edge cases
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                   Detect unintended sideeffects as sometimes developers can accidently change common functionality adversly effecting existing features without realising as delivery conditions are not always ideal and as humans we make mistakes in hurry of delivery.
                  </ListItem>
                </List>
                <Alert variant="filled" severity="warning">
                  Adding Unit Tests and Integration Tests are critical for success of any product in long run and should not be compromised to achieve speed of delivery as doing so can bite us in the future
                  <br/><br/>
                  Adding test coverage reports also helps us to increase coverage but should not become a hinderance to delivery by setting unrealistic coverage standards-
                </Alert>
                <br/>
                Standalone functionality of functions or API behaviours based on input payload should be covered in unit tests
                For example let us say we have a endpoint '/posts/create' we should add a unit test to see how this endpoint will behave when different payloads are passed to it and will look as following:
                <br/><br/>
                <CodeBlock
                  text={codeUnitTests}
                  language={'javascript'}
                  showLineNumbers={true}
                  theme={dracula}
                />
                <br/>
              </Typography>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                What are Integration Tests?
              </Typography>
              <Typography variant="body1" align="left">
                Integration Tests are the next phase of testing after unit tests
               <br/>
                They validate when different parts of a software architecture interact with one another they function as intended and smoothly
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Why Integration Tests and what to cover?
              </Typography>
              <Typography variant="body1" align="left">
                When Communication happens between two microservices, they might be working fine in isolated but can break when working together, we need to ensure that system runs smoothly when multiple services communicate with one another
                <br/>
                <br/>
                for example let us consider the following sequence between two microservices <b>Post Service</b> and <b>Validate Service</b>:
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    let us say we had both Integration and Unit Tests Passing between <b>Post service</b> and <b>Validate service</b>, 
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    then we modified the <b>Validate service</b> to accept version with api request
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    now in isolation the <b>Validate service</b> will work fine when we check the endpoint after updating the unit tests only
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    but when working with <b>Post Service</b> it will throw error because now the integration breaks, hence highlighting the necessity for integration tests as in real life systems microservices dont work in isolation and interact with one another              
                  </ListItem>
                </List>
                <br/>
                <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '6vw'} }}>
                <img
                    srcSet={integrationTestsPng}
                    alt={"softwareDevelopmentLifecycle"}
                    loading="lazy"
                  />
                </ImageListItem>
                For a given flow when multiple sevices interact with one another we need to check how this services work together.
                <br/>let us say we have two microservices wherein one microservice will call another, we need to <b>validate the output when both these services interact with one another</b>, will look as something like below:
                <br/><br/>
                <CodeBlock
                  text={codeIntegrationTests}
                  language={'javascript'}
                  showLineNumbers={true}
                  theme={dracula}
                />
                <br/>
              </Typography>

              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Test Driven Development VS Traditional Development Approach
              </Typography>
              <Typography variant="body1" align="left">
                Test Driven Development means we first write <b>unit test and then write the code</b> to make the functionality work in comparison to the traditional approach of first writing code and then adding tests for the code.
                <br/>
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                  <ListItem sx={{ display: 'list-item' }}>
                    We first consider the possible usecases that can be associated with a feature and write unit tests with this usecases and our expected output                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    Then we write the code to make the functionality to work as per the usecases we wrote
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    After which we run the tests and expect them to pass, if few of them fail we fix the code to make them work 
                  </ListItem>
                </List>
                <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '6vw'} }}>
                <img
                    srcSet={testDrivenSoftwareDevelopmentLifecyclePng}
                    alt={"testDrivenDevelopment"}
                    loading="lazy"
                  />
              </ImageListItem>
                This approach ensure that we cover all happy path usecases, edge cases usecases for a feature being developed before jumping into the coding aspect and is cleaner in comparison to just writting happy path code first and then later on handling edge cases or never until a bug is reported.
              </Typography>
          </Box>
        </Container>
    )
};